import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { COLORS } from '@/config/colors';
import { HUD } from '@/config/hud';
import { PLAYER } from '@/config/player';
import { UI_FONT } from './widgets';

const TICK_W = 7;
const TICK_H = 22;
const TICK_GAP = 2;
const CELL_GAP = 7;
const TICK_HP = 5;

function readout(fill: number): Text {
  return new Text({
    text: '',
    style: new TextStyle({ fontFamily: UI_FONT, fontSize: 14, fill, letterSpacing: 1 }),
  });
}

/**
 * Battery gauge (docs/12 C1): cells of 25 HP, five 5-HP ticks per cell,
 * numeric readout, and a YELLOW armor frame + counter when armor is up.
 * Redraws only when hp / max / armor change.
 */
export class BatteryGauge {
  readonly view = new Container();
  private readonly bar = new Graphics();
  private readonly hpText = readout(COLORS.hudHp);
  private readonly armorText = readout(COLORS.hudArmor);
  private lastHp = -1;
  private lastMaxHp = -1;
  private lastArmor = -1;
  /** Armor pickup pulse — decays in update() */
  private flash = 0;
  /** Right edge of the row (local px) — the HUD keeps the top band clear of it */
  rowRight = 0;

  constructor() {
    this.view.y = HUD.rows.hp;
    this.view.addChild(this.bar, this.hpText, this.armorText);
  }

  /** Returns true when the row width changed. */
  set(hp: number, maxHp: number, armor: number): boolean {
    if (hp === this.lastHp && maxHp === this.lastMaxHp && armor === this.lastArmor) return false;
    this.lastHp = hp;
    this.lastMaxHp = maxHp;
    this.lastArmor = armor;

    const cells = Math.max(1, Math.round(maxHp / PLAYER.cellHp));
    const ticksPerCell = PLAYER.cellHp / TICK_HP;
    const cellW = ticksPerCell * (TICK_W + TICK_GAP) - TICK_GAP;
    const innerW = cells * (cellW + CELL_GAP) + 3;
    const caseH = TICK_H + 12;

    const g = this.bar;
    g.clear();
    // Armor: yellow outer frame — damage eats this layer first
    if (armor > 0) {
      g.roundRect(-4, -4, innerW + 14, caseH + 8, 10)
        .stroke({ color: COLORS.hudArmor, width: 3 });
    }
    // Casing + terminal nub
    g.roundRect(0, 0, innerW + 4, caseH, 8)
      .fill(0x221c30)
      .stroke({ color: COLORS.outline, width: 3 });
    g.roundRect(innerW + 4, caseH / 2 - 7, 6, 14, 3)
      .fill(0x221c30)
      .stroke({ color: COLORS.outline, width: 3 });

    // Ticks: 5 HP each, grouped into 25 HP cells
    const litTicks = Math.ceil(Math.max(0, hp) / TICK_HP);
    for (let c = 0; c < cells; c++) {
      const cx = 4 + c * (cellW + CELL_GAP);
      for (let i = 0; i < ticksPerCell; i++) {
        const lit = c * ticksPerCell + i < litTicks;
        g.roundRect(cx + i * (TICK_W + TICK_GAP), (caseH - TICK_H) / 2, TICK_W, TICK_H, 2)
          .fill(lit ? COLORS.hudHp : 0x161221);
      }
    }

    // Numeric readout + armor counter
    this.hpText.text = `${Math.max(0, Math.ceil(hp))}/${maxHp}`;
    this.hpText.position.set(innerW + 18, caseH / 2 - 9);
    this.armorText.text = armor > 0 ? `${armor}` : '';
    let right = this.hpText.x + this.hpText.width;
    if (armor > 0) {
      // Tiny drawn shield (font-independent) + count
      const sx = innerW + 24 + this.hpText.width + 8;
      const sy = caseH / 2;
      g.poly([sx, sy - 9, sx + 8, sy - 5, sx + 8, sy + 2, sx, sy + 9, sx - 8, sy + 2, sx - 8, sy - 5])
        .fill(COLORS.hudArmor);
      this.armorText.position.set(sx + 12, sy - 9);
      right = this.armorText.x + this.armorText.width;
    }
    const changed = Math.abs(right - this.rowRight) > 1;
    this.rowRight = right;
    return changed;
  }

  /** Armor pickup feedback: short scale pulse on the battery block. */
  flashArmor(): void {
    this.flash = 1;
    this.lastArmor = -1; // force frame redraw
  }

  update(dt: number): void {
    if (this.flash <= 0) return;
    this.flash = Math.max(0, this.flash - dt * 2.5);
    const pulse = 1 + this.flash * 0.08;
    this.bar.scale.set(pulse);
    this.armorText.scale.set(pulse * 1.1);
  }
}
